import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../../../common/Loader";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import { ConfirmDialog } from "../../../common/dashboard/ConfirmDialog";
import {
  useDeleteStripeConnectForSandboxChamberMutation,
  useGetChamberMemberProSettingsQuery,
  useRefreshChamberMemberProSenderDomainStatusForChamberMutation,
  useRefreshStripeConnectStatusForChamberMutation,
  useRemoveChamberMemberProSenderDomainForChamberMutation,
  useSendChamberMemberProSenderDomainTestEmailMutation,
  useStartStripeConnectForChamberMutation,
  useStartChamberMemberProSenderDomainVerificationMutation,
  useUpdateChamberMemberProSettingsForChamberMutation,
} from "../../../../graphql/types";
import useGetUserFromUserIdInAuth from "../../../../hooks/useGetUserFromUserIdInAuth";
import { GlobalSettingsSectionId, SettingsTabs, TabId } from "./common";
import {
  AffinityProgramSetupTab,
  EmailTab,
  GlobalSettingsTab,
  MembershipTab,
} from "./tabs";
import { ChamberSettingsFormState } from "./settingsData";
import {
  mapApiSettingsToFormState,
  mapFormStateToApiSettingsInput,
} from "./settingsMappers";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";

const formatSavedAt = (savedAtUtcMilli?: number | null) => {
  if (!savedAtUtcMilli) {
    return "Loaded defaults";
  }

  return `Last Saved ${new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(savedAtUtcMilli))}`;
};

const getChamberGlobalsSetup = (
  setup: ChamberSettingsFormState["setup"]
): Omit<
  ChamberSettingsFormState["setup"],
  "membershipApplicationCommunicationOptions"
> => ({
  fiscalYearStartMonth: setup.fiscalYearStartMonth,
  fiscalYearStartDay: setup.fiscalYearStartDay,
  currency: setup.currency,
  timeZone: setup.timeZone,
  membershipRenewalFixedDateEnabled:
    setup.membershipRenewalFixedDateEnabled,
  membershipRenewalFixedDateMonth: setup.membershipRenewalFixedDateMonth,
  membershipRenewalFixedDateDay: setup.membershipRenewalFixedDateDay,
});

const getSectionScopedSettings = (
  baseSettings: ChamberSettingsFormState,
  sourceSettings: ChamberSettingsFormState,
  sectionId: GlobalSettingsSectionId
): ChamberSettingsFormState => {
  const baseWithSavedTimestamp = {
    ...baseSettings,
    updatedAtUtcMilli: sourceSettings.updatedAtUtcMilli,
  };

  switch (sectionId) {
    case "chamberGlobals":
      return {
        ...baseWithSavedTimestamp,
        setup: {
          ...baseWithSavedTimestamp.setup,
          ...getChamberGlobalsSetup(sourceSettings.setup),
        },
        membership: {
          ...baseWithSavedTimestamp.membership,
          globalToggleState: {
            ...baseWithSavedTimestamp.membership.globalToggleState,
            prorate: sourceSettings.membership.globalToggleState.prorate,
          },
        },
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          provincialChamberFee:
            sourceSettings.paymentsAndTaxes.provincialChamberFee,
        },
      };
    case "taxSettings":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          taxRegistrationNumber:
            sourceSettings.paymentsAndTaxes.taxRegistrationNumber,
          invoiceTaxSettings: sourceSettings.paymentsAndTaxes.invoiceTaxSettings,
        },
      };
    case "accountAllocation":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          accountAllocations:
            sourceSettings.paymentsAndTaxes.accountAllocations,
        },
      };
    case "paymentOptions":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          manualPaymentMethods:
            sourceSettings.paymentsAndTaxes.manualPaymentMethods,
          stripeConnect: sourceSettings.paymentsAndTaxes.stripeConnect,
          cardProcessingFee: sourceSettings.paymentsAndTaxes.cardProcessingFee,
          monthlyMembershipAdministrativeFee:
            sourceSettings.paymentsAndTaxes.monthlyMembershipAdministrativeFee,
          eventTicketCreditCardPaymentMode:
            sourceSettings.paymentsAndTaxes.eventTicketCreditCardPaymentMode,
          chamberGroupInsurancePayment:
            sourceSettings.paymentsAndTaxes.chamberGroupInsurancePayment,
        },
      };
    case "invoicingOptions":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          invoiceStartNumber:
            sourceSettings.paymentsAndTaxes.invoiceStartNumber,
          membershipDiscount:
            sourceSettings.paymentsAndTaxes.membershipDiscount,
          oneTimeOnboardingFee:
            sourceSettings.paymentsAndTaxes.oneTimeOnboardingFee,
          invoicePaymentTerms:
            sourceSettings.paymentsAndTaxes.invoicePaymentTerms,
          overdueInvoiceLateFeePercentage:
            sourceSettings.paymentsAndTaxes.overdueInvoiceLateFeePercentage,
        },
      };
    case "memberCommunication":
      return {
        ...baseWithSavedTimestamp,
        setup: {
          ...baseWithSavedTimestamp.setup,
          membershipApplicationCommunicationOptions:
            sourceSettings.setup.membershipApplicationCommunicationOptions,
        },
      };
    case "membershipSettings":
      return {
        ...baseWithSavedTimestamp,
        membership: sourceSettings.membership,
      };
    case "emailCommunications":
      return {
        ...baseWithSavedTimestamp,
        email: sourceSettings.email,
      };
    default:
      return baseWithSavedTimestamp;
  }
};

const getSectionValue = (
  settings: ChamberSettingsFormState,
  sectionId: GlobalSettingsSectionId
) => {
  switch (sectionId) {
    case "chamberGlobals":
      return {
        setup: getChamberGlobalsSetup(settings.setup),
        prorate: settings.membership.globalToggleState.prorate,
        provincialChamberFee: settings.paymentsAndTaxes.provincialChamberFee,
      };
    case "taxSettings":
      return {
        taxRegistrationNumber: settings.paymentsAndTaxes.taxRegistrationNumber,
        invoiceTaxSettings: settings.paymentsAndTaxes.invoiceTaxSettings,
      };
    case "accountAllocation":
      return settings.paymentsAndTaxes.accountAllocations;
    case "paymentOptions":
      return {
        manualPaymentMethods: settings.paymentsAndTaxes.manualPaymentMethods,
        stripeConnect: settings.paymentsAndTaxes.stripeConnect,
        cardProcessingFee: settings.paymentsAndTaxes.cardProcessingFee,
        monthlyMembershipAdministrativeFee:
          settings.paymentsAndTaxes.monthlyMembershipAdministrativeFee,
        eventTicketCreditCardPaymentMode:
          settings.paymentsAndTaxes.eventTicketCreditCardPaymentMode,
        chamberGroupInsurancePayment:
          settings.paymentsAndTaxes.chamberGroupInsurancePayment,
      };
    case "invoicingOptions":
      return {
        invoiceStartNumber: settings.paymentsAndTaxes.invoiceStartNumber,
        membershipDiscount: settings.paymentsAndTaxes.membershipDiscount,
        oneTimeOnboardingFee:
          settings.paymentsAndTaxes.oneTimeOnboardingFee,
        invoicePaymentTerms: settings.paymentsAndTaxes.invoicePaymentTerms,
        overdueInvoiceLateFeePercentage:
          settings.paymentsAndTaxes.overdueInvoiceLateFeePercentage,
      };
    case "memberCommunication":
      return settings.setup.membershipApplicationCommunicationOptions;
    case "membershipSettings":
      return settings.membership;
    case "emailCommunications":
      return settings.email;
    default:
      return null;
  }
};

const ChamberSettings = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>("globalSettings");
  const [settings, setSettings] = useState<ChamberSettingsFormState | null>(
    null
  );
  const [lastSavedSettings, setLastSavedSettings] =
    useState<ChamberSettingsFormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [stripeConnectError, setStripeConnectError] = useState<string | null>(
    null
  );
  const [senderDomainError, setSenderDomainError] = useState<string | null>(
    null
  );
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  const [savedAtUtcMilli, setSavedAtUtcMilli] = useState<number | null>(null);
  const [activeSavingSection, setActiveSavingSection] =
    useState<GlobalSettingsSectionId | null>(null);
  const [lastSavedSection, setLastSavedSection] =
    useState<GlobalSettingsSectionId | null>(null);
  const [lastFailedSection, setLastFailedSection] =
    useState<GlobalSettingsSectionId | null>(null);
  // POL-026(b): destructive confirms ride the shared ConfirmDialog instead of
  // native window.confirm (copy preserved verbatim, mutation loading wired).
  const [stripeDisconnectConfirmOpen, setStripeDisconnectConfirmOpen] =
    useState(false);
  const [removeSenderDomainConfirmOpen, setRemoveSenderDomainConfirmOpen] =
    useState(false);
  // B15 guardrail: the Affinity tab manages its own drafts outside the
  // settings form state; it reports dirtiness so the route guard covers it.
  const [affinityDirty, setAffinityDirty] = useState(false);
  const [emailSubscriberTargetDirty, setEmailSubscriberTargetDirty] =
    useState(false);

  const loggedInUser = useGetUserFromUserIdInAuth();
  const chamberId = loggedInUser?.chamberId;

  const {
    data: settingsData,
    loading: settingsLoading,
    error: settingsError,
    refetch,
  } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId: chamberId || "" },
    skip: !chamberId,
    fetchPolicy: "network-only",
  });

  const [updateChamberMemberProSettings, { loading: saving }] =
    useUpdateChamberMemberProSettingsForChamberMutation();
  const [startStripeConnectForChamber, { loading: startingStripeConnect }] =
    useStartStripeConnectForChamberMutation();
  const [
    refreshStripeConnectStatusForChamber,
    { loading: refreshingStripeConnect },
  ] = useRefreshStripeConnectStatusForChamberMutation();
  const [
    deleteStripeConnectForSandboxChamber,
    { loading: deletingStripeConnect },
  ] = useDeleteStripeConnectForSandboxChamberMutation();
  const [
    startChamberMemberProSenderDomainVerification,
    { loading: startingSenderDomainVerification },
  ] = useStartChamberMemberProSenderDomainVerificationMutation();
  const [
    refreshChamberMemberProSenderDomainStatusForChamber,
    { loading: refreshingSenderDomainStatus },
  ] = useRefreshChamberMemberProSenderDomainStatusForChamberMutation();
  const [
    removeChamberMemberProSenderDomainForChamber,
    { loading: removingSenderDomain },
  ] = useRemoveChamberMemberProSenderDomainForChamberMutation();
  const [
    sendChamberMemberProSenderDomainTestEmail,
    { loading: sendingSenderDomainTestEmail },
  ] = useSendChamberMemberProSenderDomainTestEmailMutation();

  const applySettingsFromApi = useCallback((
    apiSettings: Parameters<typeof mapApiSettingsToFormState>[0],
    savedAtFallbackUtcMilli: number | null = null
  ) => {
    const mappedSettings = mapApiSettingsToFormState(apiSettings);
    setSettings(mappedSettings);
    setLastSavedSettings(mappedSettings);
    setSavedAtUtcMilli(
      mappedSettings.updatedAtUtcMilli || savedAtFallbackUtcMilli
    );
    setSaveError(null);
    setSenderDomainError(null);
    setActiveSavingSection(null);
    setLastSavedSection(null);
    setLastFailedSection(null);
  }, []);

  useEffect(() => {
    if (!settingsData?.getChamberMemberProSettings) {
      return;
    }

    applySettingsFromApi(settingsData.getChamberMemberProSettings);
  }, [applySettingsFromApi, settingsData]);

  useEffect(() => {
    const stripeConnectResult = searchParams.get("stripeConnect");

    if (stripeConnectResult === "success") {
      setActiveTab("globalSettings");
      setStatusNotice("Stripe connection completed successfully.");
      setStripeConnectError(null);
      return;
    }

    if (stripeConnectResult === "error") {
      setActiveTab("globalSettings");
      setStripeConnectError(
        searchParams.get("message") ||
          "Stripe connection could not be completed."
      );
    }
  }, [searchParams]);

  const hasUnsavedChanges = useMemo(() => {
    if (!settings || !lastSavedSettings) {
      return false;
    }

    return (
      JSON.stringify(settings) !== JSON.stringify(lastSavedSettings) ||
      emailSubscriberTargetDirty
    );
  }, [emailSubscriberTargetDirty, settings, lastSavedSettings]);

  const updateSettings = (
    updater: (currentSettings: ChamberSettingsFormState) => ChamberSettingsFormState
  ) => {
    setSettings((currentSettings) =>
      currentSettings ? updater(currentSettings) : currentSettings
    );
    setSaveError(null);
    setStatusNotice(null);
    setLastFailedSection(null);
  };

  const isSectionDirty = (sectionId: GlobalSettingsSectionId) => {
    if (!settings || !lastSavedSettings) {
      return false;
    }

    return (
      JSON.stringify(getSectionValue(settings, sectionId)) !==
      JSON.stringify(getSectionValue(lastSavedSettings, sectionId))
    );
  };

  const handleSaveSection = async (sectionId: GlobalSettingsSectionId) => {
    if (!settings || !lastSavedSettings || !chamberId) {
      return;
    }

    const scopedSettingsForSave = getSectionScopedSettings(
      lastSavedSettings,
      settings,
      sectionId
    );

    try {
      setActiveSavingSection(sectionId);
      setLastSavedSection(null);
      setLastFailedSection(null);
      const result = await updateChamberMemberProSettings({
        variables: {
          chamberId,
          chamberMemberProSettings:
            mapFormStateToApiSettingsInput(scopedSettingsForSave),
        },
      });

      const savedSettings =
        result.data?.updateChamberMemberProSettingsForChamber
          .chamberMemberProSettings;

      if (!savedSettings) {
        throw new Error("Settings update returned no data");
      }

      const mappedSavedSettings = mapApiSettingsToFormState(savedSettings);
      setLastSavedSettings(mappedSavedSettings);
      setSettings((currentSettings) =>
        currentSettings
          ? getSectionScopedSettings(
              currentSettings,
              mappedSavedSettings,
              sectionId
            )
          : mappedSavedSettings
      );
      setSavedAtUtcMilli(mappedSavedSettings.updatedAtUtcMilli || Date.now());
      setSaveError(null);
      setStatusNotice("Section saved.");
      setLastSavedSection(sectionId);
      setLastFailedSection(null);
      toast.success("Settings section saved.");
    } catch (error: any) {
      setSaveError(error?.message || "Failed to save chamber settings");
      setLastFailedSection(sectionId);
      toast.error("Failed to save settings section.");
    } finally {
      setActiveSavingSection(null);
    }
  };

  const handleResetSection = (sectionId: GlobalSettingsSectionId) => {
    if (!lastSavedSettings) {
      return;
    }

    setSettings((currentSettings) =>
      currentSettings
        ? getSectionScopedSettings(
            currentSettings,
            lastSavedSettings,
            sectionId
          )
        : currentSettings
    );
    setSaveError(null);
    setStatusNotice(null);
    setLastSavedSection(null);
    setLastFailedSection(null);
  };

  const handleStartStripeConnect = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setStripeConnectError(
        "Save or reset unsaved settings before updating Stripe Connect."
      );
      return;
    }

    try {
      setStripeConnectError(null);
      setStatusNotice(null);

      const result = await startStripeConnectForChamber({
        variables: { chamberId },
      });

      const response = result.data?.startStripeConnectForChamber;

      if (!response?.chamberMemberProSettings) {
        throw new Error("Stripe Connect setup returned no settings");
      }

      applySettingsFromApi(response.chamberMemberProSettings, Date.now());

      if (response.stripeConnectOAuthUrl) {
        window.location.href = response.stripeConnectOAuthUrl;
        return;
      }

      setStatusNotice("Stripe connection updated.");
    } catch (error: any) {
      setStripeConnectError(
        error?.message || "Failed to start Stripe Connect setup"
      );
    }
  };

  const handleRefreshStripeConnect = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setStripeConnectError(
        "Save or reset unsaved settings before updating Stripe Connect."
      );
      return;
    }

    try {
      setStripeConnectError(null);
      setStatusNotice(null);

      const result = await refreshStripeConnectStatusForChamber({
        variables: { chamberId },
      });

      const refreshedSettings =
        result.data?.refreshStripeConnectStatusForChamber
          .chamberMemberProSettings;

      if (!refreshedSettings) {
        throw new Error("Stripe Connect refresh returned no settings");
      }

      applySettingsFromApi(refreshedSettings, Date.now());
      setStatusNotice("Stripe connection status refreshed.");
    } catch (error: any) {
      setStripeConnectError(
        error?.message || "Failed to refresh Stripe Connect status"
      );
    }
  };

  const handleDeleteStripeConnect = () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setStripeConnectError(
        "Save or reset unsaved settings before updating Stripe Connect."
      );
      return;
    }

    setStripeDisconnectConfirmOpen(true);
  };

  const confirmDeleteStripeConnect = async () => {
    if (!chamberId) {
      return;
    }

    try {
      setStripeConnectError(null);
      setStatusNotice(null);

      const result = await deleteStripeConnectForSandboxChamber({
        variables: { chamberId },
      });

      const deletedSettings =
        result.data?.deleteStripeConnectForSandboxChamber
          .chamberMemberProSettings;

      if (!deletedSettings) {
        throw new Error("Stripe Connect delete returned no settings");
      }

      applySettingsFromApi(deletedSettings, Date.now());
      setStatusNotice("Stripe test connection deleted.");
    } catch (error: any) {
      setStripeConnectError(
        error?.message || "Failed to delete Stripe Connect test connection"
      );
    } finally {
      // Close in a finally so an error still dismisses the dialog and the
      // Stripe section error banner takes over.
      setStripeDisconnectConfirmOpen(false);
    }
  };

  const handleStartSenderDomainVerification = async ({
    domain,
    senderEmail,
  }: {
    domain: string;
    senderEmail: string;
  }) => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        "Save or reset unsaved settings before updating the email domain."
      );
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result = await startChamberMemberProSenderDomainVerification({
        variables: { chamberId, domain, senderEmail },
      });
      const updatedSettings =
        result.data?.startChamberMemberProSenderDomainVerification
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Email domain setup returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Email domain setup started.");
    } catch (error: any) {
      setSenderDomainError(error?.message || "Failed to set up email domain");
    }
  };

  const handleRefreshSenderDomainStatus = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        "Save or reset unsaved settings before updating the email domain."
      );
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result =
        await refreshChamberMemberProSenderDomainStatusForChamber({
          variables: { chamberId },
        });
      const updatedSettings =
        result.data?.refreshChamberMemberProSenderDomainStatusForChamber
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Email domain status check returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Email domain status checked.");
    } catch (error: any) {
      setSenderDomainError(
        error?.message || "Failed to check email domain status"
      );
    }
  };

  const handleRemoveSenderDomain = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        "Save or reset unsaved settings before updating the email domain."
      );
      return;
    }

    setRemoveSenderDomainConfirmOpen(true);
  };

  const confirmRemoveSenderDomain = async () => {
    if (!chamberId) {
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result = await removeChamberMemberProSenderDomainForChamber({
        variables: { chamberId },
      });
      const updatedSettings =
        result.data?.removeChamberMemberProSenderDomainForChamber
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Email domain removal returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Email domain removed.");
    } catch (error: any) {
      setSenderDomainError(error?.message || "Failed to remove email domain");
    } finally {
      // Close in a finally so an error still dismisses the dialog and the
      // section error banner (senderDomainError) takes over.
      setRemoveSenderDomainConfirmOpen(false);
    }
  };

  const handleSendSenderDomainTestEmail = async (recipientEmail: string) => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        "Save or reset unsaved settings before sending a test email."
      );
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result = await sendChamberMemberProSenderDomainTestEmail({
        variables: { chamberId, recipientEmail },
      });
      const updatedSettings =
        result.data?.sendChamberMemberProSenderDomainTestEmail
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Test email returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Test email sent.");
    } catch (error: any) {
      setSenderDomainError(error?.message || "Failed to send test email");
    }
  };

  const loading = !loggedInUser || (Boolean(chamberId) && settingsLoading);
  const loadError = settingsError;

  if (loading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        <p className="font-semibold">Unable to load chamber settings.</p>
        <p className="mt-1">{loadError.message}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!chamberId || !settings) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
        Chamber settings are unavailable because no chamber is linked to this
        user.
      </div>
    );
  }

  const subscriberTargets =
    settingsData?.getChamberMemberProSettings.email.subscriberTargets || [];
  const currentCalendarYear = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: normalizeCmpTimeZone(
        settingsData?.getChamberMemberProSettings.setup.timeZone
      ).timeZone,
      year: "numeric",
    }).format(new Date())
  );
  const currentEmailSubscriberTarget =
    subscriberTargets.find(
      (target) => target.calendarYear === currentCalendarYear
    ) || null;

  if (!currentEmailSubscriberTarget) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        The email subscriber target could not be initialized. Try reloading the
        Chamber Setup page.
      </div>
    );
  }

  const makeSectionControls = (
    sectionId: GlobalSettingsSectionId
  ) => ({
    isDirty: isSectionDirty(sectionId),
    saving: saving && activeSavingSection === sectionId,
    lastSavedLabel: formatSavedAt(savedAtUtcMilli),
    onSave: () => handleSaveSection(sectionId),
    onReset: () => handleResetSection(sectionId),
    statusText:
      lastSavedSection === sectionId && statusNotice ? statusNotice : null,
    errorText:
      lastFailedSection === sectionId
        ? saveError
        : null,
  });
  const sectionControls = {
    chamberGlobals: makeSectionControls("chamberGlobals"),
    taxSettings: makeSectionControls("taxSettings"),
    accountAllocation: makeSectionControls("accountAllocation"),
    paymentOptions: makeSectionControls("paymentOptions"),
    invoicingOptions: makeSectionControls("invoicingOptions"),
    memberCommunication: makeSectionControls("memberCommunication"),
    membershipSettings: makeSectionControls("membershipSettings"),
    emailCommunications: makeSectionControls("emailCommunications"),
  };

  return (
    <div>
      <UnsavedChangesPrompt
        when={(hasUnsavedChanges || affinityDirty) && !saving}
      />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <SettingsTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="mt-6 w-full px-4 sm:px-0">
        <div className={activeTab === "globalSettings" ? "block" : "hidden"}>
          <GlobalSettingsTab
            settings={settings}
            isSandboxChamber={Boolean(loggedInUser?.chamberInfo?.isSandbox)}
            onChange={(nextSettings) => updateSettings(() => nextSettings)}
            sectionControls={sectionControls}
            onStartStripeConnect={handleStartStripeConnect}
            onRefreshStripeConnect={handleRefreshStripeConnect}
            onDeleteStripeConnect={handleDeleteStripeConnect}
            stripeConnectActionLoading={startingStripeConnect}
            stripeConnectRefreshLoading={refreshingStripeConnect}
            stripeConnectDeleteLoading={deletingStripeConnect}
            stripeConnectError={stripeConnectError}
            onStartSenderDomainVerification={
              handleStartSenderDomainVerification
            }
            onRefreshSenderDomainStatus={handleRefreshSenderDomainStatus}
            onRemoveSenderDomain={handleRemoveSenderDomain}
            onSendSenderDomainTestEmail={handleSendSenderDomainTestEmail}
            senderDomainActionLoading={startingSenderDomainVerification}
            senderDomainRefreshLoading={refreshingSenderDomainStatus}
            senderDomainRemoveLoading={removingSenderDomain}
            senderDomainTestEmailLoading={sendingSenderDomainTestEmail}
            senderDomainError={senderDomainError}
            hasUnsavedSettingsChanges={hasUnsavedChanges}
          />
        </div>
        <div className={activeTab === "membershipSettings" ? "block" : "hidden"}>
          <MembershipTab
            settings={settings.membership}
            accountAllocations={settings.paymentsAndTaxes.accountAllocations}
            sectionControls={sectionControls.membershipSettings}
            onChange={(membership) =>
              updateSettings((currentSettings) => ({
                ...currentSettings,
                membership,
              }))
            }
          />
        </div>
        <div
          className={activeTab === "emailCommunications" ? "block" : "hidden"}
        >
          <EmailTab
            chamberId={chamberId}
            settings={settings.email}
            sectionControls={sectionControls.emailCommunications}
            subscriberTarget={currentEmailSubscriberTarget}
            onSubscriberTargetDirtyChange={setEmailSubscriberTargetDirty}
            onChange={(email) =>
              updateSettings((currentSettings) => ({
                ...currentSettings,
                email,
              }))
            }
          />
        </div>
        <div
          className={
            activeTab === "affinityProgramSetup" ? "block" : "hidden"
          }
        >
          <AffinityProgramSetupTab
            chamberId={chamberId}
            lastSavedLabel={formatSavedAt(savedAtUtcMilli)}
            onDirtyChange={setAffinityDirty}
          />
        </div>
      </div>

      <ConfirmDialog
        open={stripeDisconnectConfirmOpen}
        variant="destructive"
        title="Delete this sandbox chamber's Stripe test connection?"
        message="This disconnects the test Stripe account from the platform and clears the saved connection."
        loading={deletingStripeConnect}
        onConfirm={confirmDeleteStripeConnect}
        onClose={() => setStripeDisconnectConfirmOpen(false)}
      />
      <ConfirmDialog
        open={removeSenderDomainConfirmOpen}
        variant="destructive"
        title="Remove this email domain setup from Chamber Member Pro?"
        message="DNS records in your domain provider will not be changed."
        loading={removingSenderDomain}
        onConfirm={confirmRemoveSenderDomain}
        onClose={() => setRemoveSenderDomainConfirmOpen(false)}
      />
    </div>
  );
};

export default ChamberSettings;
